import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Check, Clock, AlertCircle, CreditCard } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface SubscriptionStatus {
  plan: string;
  isTrial: boolean;
  trialDaysLeft?: number;
  trialExpiresAt?: string;
  hasActiveSubscription: boolean;
  needsSubscription: boolean;
  isExempt?: boolean;
}

export default function Subscription() {
  const { toast } = useToast();

  // Get subscription status
  const { data: status, isLoading } = useQuery<SubscriptionStatus>({
    queryKey: ["/api/subscription/status"],
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Create checkout session mutation
  const checkoutMutation = useMutation({
    mutationFn: async ({ priceId, planType, planDetails }: { priceId: string; planType: string; planDetails?: any }) => {
      const response = await apiRequest("/api/create-checkout-session", {
        method: "POST",
        body: { priceId, planType, planDetails }
      });
      return response;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({
          title: "Redirecting to checkout...",
          description: "Please complete your payment to activate your subscription.",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Checkout Failed",
        description: error.message || "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubscribe = (planType: 'monthly' | 'yearly', billingPeriod: 'month' | 'year') => {
    // planType: 'monthly' = Monthly Plan (1 domain), 'yearly' = Pro Plan (10 domains)
    // billingPeriod: 'month' = monthly billing, 'year' = annual billing
    
    // Send plan details to backend - backend will handle Stripe Price ID lookup
    // This way we don't need Price IDs in the frontend
    const planDetails = {
      planType: planType, // 'monthly' or 'yearly' (plan tier)
      billingPeriod: billingPeriod, // 'month' or 'year' (billing frequency)
      planName: planType === 'monthly' ? 'monthly' : 'pro',
      amount: billingPeriod === 'month' 
        ? (planType === 'monthly' ? 2900 : 9900) // $29.00 or $99.00 in cents
        : (planType === 'monthly' ? 27900 : 95000) // $279.00 or $950.00 in cents
    };
    
    checkoutMutation.mutate({ 
      priceId: 'backend-handled', // Backend will determine the correct Price ID
      planType: billingPeriod === 'year' ? 'yearly' : 'monthly',
      planDetails: planDetails
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="h-32 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  const isTrialActive = status?.isTrial && status.trialDaysLeft && status.trialDaysLeft > 0;
  const trialExpired = status?.isTrial && (!status.trialDaysLeft || status.trialDaysLeft <= 0);
  const hasSubscription = status?.hasActiveSubscription;

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold mb-4">Subscription & Plans</h1>
        <p className="text-xl text-muted-foreground">
          Manage your subscription and choose the perfect plan for your needs
        </p>
        {isTrialActive && status.trialDaysLeft && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <Clock className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {status.trialDaysLeft} day{status.trialDaysLeft !== 1 ? 's' : ''} left in your trial
            </span>
          </div>
        )}
        {trialExpired && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-900 dark:text-red-100">
              Your trial has expired. Subscribe now to continue.
            </span>
          </div>
        )}
      </div>

      {/* Current Status Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Current Status</CardTitle>
        </CardHeader>
        <CardContent>
          {hasSubscription ? (
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              <span className="font-semibold">Active Subscription</span>
              <Badge variant="default">{status.plan}</Badge>
            </div>
          ) : isTrialActive ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                <span className="font-semibold">Trial Period Active</span>
                <Badge variant="secondary">Trial</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {status.trialDaysLeft} day{status.trialDaysLeft !== 1 ? 's' : ''} remaining in your trial
              </p>
              {status.trialExpiresAt && (
                <p className="text-xs text-muted-foreground">
                  Trial expires on {new Date(status.trialExpiresAt).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : trialExpired ? (
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span className="font-semibold text-red-500">Trial Expired</span>
              <p className="text-sm text-muted-foreground ml-4">
                Your 2-day trial has ended. Please subscribe to continue using the service.
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              <span className="font-semibold">No Active Subscription</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription Plans - Always Show */}
      {!status?.isExempt && (
        <div className="mb-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-2">Available Plans</h2>
            <p className="text-muted-foreground">Choose the plan that best fits your needs</p>
          </div>
          
          {/* Monthly Plans */}
          <div className="mb-8">
            <h3 className="text-xl font-bold mb-4">Monthly Billing</h3>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Monthly Plan - 1 Domain */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Monthly Plan</CardTitle>
                <CardDescription>Billed monthly, cancel anytime</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="text-4xl font-bold">$29<span className="text-lg text-muted-foreground">/month</span></div>
                  <div className="text-base font-bold text-orange-600 dark:text-orange-400 mt-2">
                    ⚠️ 1 Domain Only
                  </div>
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">
                    Monthly Plan: <strong className="font-bold">$29/month</strong> for <strong className="font-bold">1 domain</strong> monitoring
                  </div>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span><strong>1 domain</strong> monitoring</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Unlimited security scans</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Real-time threat monitoring</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Advanced compliance reports</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Priority email support</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>JavaScript integrity monitoring</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Security header analysis</span>
                  </li>
                </ul>
                <Button
                  className="w-full h-12 text-lg font-bold"
                  onClick={() => handleSubscribe('monthly', 'month')}
                  disabled={checkoutMutation.isPending}
                >
                  <CreditCard className="h-5 w-5 mr-2" />
                  {checkoutMutation.isPending ? 'Processing...' : 'Subscribe via Stripe - $29/month'}
                </Button>
              </CardContent>
            </Card>

            {/* Pro Plan - 5 Domains */}
            <Card className="relative border-2 border-primary">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <Badge className="px-4 py-1 text-sm bg-primary text-primary-foreground font-bold">
                  Best Value
                </Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Pro Plan</CardTitle>
                <CardDescription>Billed monthly, 5 domains included</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="text-4xl font-bold">$99<span className="text-lg text-muted-foreground">/month</span></div>
                  <div className="text-base font-bold text-green-600 dark:text-green-400 mt-2">
                    5 domains • Best for growing businesses
                  </div>
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">
                    Pro Plan: <strong className="font-bold">$99/month</strong> for <strong className="font-bold">5 domains</strong> monitoring
                  </div>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span><strong>5 domains</strong> monitoring</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Unlimited security scans</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Real-time threat monitoring</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Advanced compliance reports</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Priority email support</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>JavaScript integrity monitoring</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Security header analysis</span>
                  </li>
                </ul>
                <Button
                  className="w-full h-12 text-lg bg-primary hover:bg-primary/90 font-bold"
                  onClick={() => handleSubscribe('yearly', 'month')}
                  disabled={checkoutMutation.isPending}
                >
                  <CreditCard className="h-5 w-5 mr-2" />
                  {checkoutMutation.isPending ? 'Processing...' : 'Subscribe via Stripe - $99/month'}
                </Button>
              </CardContent>
            </Card>
            </div>
          </div>

          {/* Yearly Plans with Discounts */}
          <div className="mb-8">
            <h3 className="text-xl font-bold mb-4">Annual Billing <span className="text-green-600 dark:text-green-400 text-base">(Save 20%)</span></h3>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Monthly Plan - Yearly */}
              <Card className="border-green-200 dark:border-green-800">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold">Monthly Plan - Annual</CardTitle>
                  <CardDescription>Billed annually, cancel anytime</CardDescription>
                  <Badge variant="secondary" className="mt-2 w-fit bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                    Save 20%
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <div className="text-4xl font-bold">$279<span className="text-lg text-muted-foreground">/year</span></div>
                    <div className="text-sm text-muted-foreground line-through">$348/year</div>
                    <div className="text-base font-bold text-green-600 dark:text-green-400 mt-1">
                      $23.25/month • Save $69/year
                    </div>
                    <div className="text-sm font-semibold text-orange-600 dark:text-orange-400 mt-2">
                      ⚠️ 1 Domain Only
                    </div>
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">
                      Monthly Plan: <strong className="font-bold">$279/year</strong> for <strong className="font-bold">1 domain</strong> monitoring
                    </div>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span><strong>1 domain</strong> monitoring</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Unlimited security scans</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Real-time threat monitoring</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Advanced compliance reports</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Priority email support</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>JavaScript integrity monitoring</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Security header analysis</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="font-semibold">20% annual savings</span>
                    </li>
                  </ul>
                  <Button
                    className="w-full h-12 text-lg font-bold bg-green-600 hover:bg-green-700"
                    onClick={() => handleSubscribe('monthly', 'year')}
                    disabled={checkoutMutation.isPending}
                  >
                    <CreditCard className="h-5 w-5 mr-2" />
                    {checkoutMutation.isPending ? 'Processing...' : 'Subscribe via Stripe - $279/year'}
                  </Button>
                </CardContent>
              </Card>

              {/* Pro Plan - Yearly */}
              <Card className="relative border-2 border-primary border-green-500">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="px-4 py-1 text-sm bg-green-600 text-white font-bold">
                    Best Value - Annual
                  </Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl font-bold">Pro Plan - Annual</CardTitle>
                  <CardDescription>Billed annually, 5 domains included</CardDescription>
                  <Badge variant="secondary" className="mt-2 w-fit bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                    Save 20%
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <div className="text-4xl font-bold">$950<span className="text-lg text-muted-foreground">/year</span></div>
                    <div className="text-sm text-muted-foreground line-through">$1,188/year</div>
                    <div className="text-base font-bold text-green-600 dark:text-green-400 mt-1">
                      $79.17/month • Save $238/year
                    </div>
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">
                      Pro Plan: <strong className="font-bold">$950/year</strong> for <strong className="font-bold">5 domains</strong> monitoring
                    </div>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span><strong>5 domains</strong> monitoring</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Unlimited security scans</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Real-time threat monitoring</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Advanced compliance reports</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Priority email support</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>JavaScript integrity monitoring</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>Security header analysis</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="font-semibold">20% annual savings</span>
                    </li>
                  </ul>
                  <Button
                    className="w-full h-12 text-lg bg-green-600 hover:bg-green-700 font-bold"
                    onClick={() => handleSubscribe('yearly', 'year')}
                    disabled={checkoutMutation.isPending}
                  >
                    <CreditCard className="h-5 w-5 mr-2" />
                    {checkoutMutation.isPending ? 'Processing...' : 'Subscribe via Stripe - $950/year'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      {status?.isExempt ? (
        <Card>
          <CardHeader>
            <CardTitle>Unlimited Access</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-2">
              <Check className="h-5 w-5 text-green-500" />
              <span className="font-semibold">Your account has unlimited access</span>
            </div>
            <p className="text-sm text-muted-foreground">
              As a member of an exempt organization, you have full access to all features without subscription requirements.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>About Your Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>• All new users receive a 2-day free trial (1 domain limit)</p>
            <p>• Monthly Plan: $29/month for 1 domain monitoring</p>
            <p>• Pro Plan: $99/month for 5 domains monitoring</p>
            <p>• Your trial starts from the day you create your account</p>
            <p>• You can subscribe at any time during your trial</p>
            <p>• Subscriptions can be cancelled at any time</p>
            <p>• Payment is processed securely through Stripe</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

